import { createLogger } from '@authe/fe-commons/logging';
import * as React from 'react';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { Badge, Button, cn, Avatar, Textarea, Dialog, DialogContent, DialogHeader, DialogTitle, ErrorState, Skeleton, EmptyState, RiskBadge, AuditTrail } from '@authe/ui-core';
import { pendingReviewCount, predictionRiskLevel, isReviewActionable, topContributions, ML_REVIEW_STATUS_LABEL, MLReviewStatus, comparePredictions, disagreements, championOf } from '@authe/fe-commons/ml';
import { EventCriticality, EventResourceType } from '@authe/fe-commons/events';
import { RiskLevel } from '@authe/fe-commons/precedence';

// src/config.ts
var AI_ASSISTANT_PACKAGE = "@authe/ai-assistant";
var log = createLogger("ai-assistant");
var DEFAULT_CHAT_ENDPOINT = "/ai/chat";

// src/model/action.ts
function actionLabel(action) {
  if (action.title) return action.title;
  switch (action.kind) {
    case "graph_overlay":
      return "Show on graph";
    case "table_filter":
      return "Filter table";
    case "navigate":
      return "Open";
    case "open_resource":
      return `Open ${action.resource.kind}`;
    case "run_query":
      return "Run query";
    case "suggest_catalog":
      return `Add ${action.suggestion.kind} "${action.suggestion.name}"`;
  }
}

// src/model/message.ts
var counter = 0;
function newId(prefix = "msg") {
  const uuid = typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now().toString(36)}-${(counter++).toString(36)}`;
  return `${prefix}_${uuid}`;
}
function messageText(message) {
  return message.parts.filter((p) => p.type === "text").map((p) => p.text).join("");
}
function messageActions(message) {
  return message.parts.filter((p) => p.type === "action").map((p) => p.action);
}
function messageCitations(message) {
  return message.parts.filter((p) => p.type === "citation").map((p) => p.citation);
}
function userMessage(text) {
  return { id: newId("u"), role: "user", parts: [{ type: "text", text }], createdAt: (/* @__PURE__ */ new Date()).toISOString() };
}
function assistantPlaceholder() {
  return { id: newId("a"), role: "assistant", parts: [], createdAt: (/* @__PURE__ */ new Date()).toISOString(), pending: true };
}

// src/transport/sse.ts
function isStreamEvent(value) {
  return typeof value === "object" && value !== null && typeof value.type === "string";
}
function createSseChatTransport(options = {}) {
  const {
    url = DEFAULT_CHAT_ENDPOINT,
    fetchImpl,
    headers,
    buildBody,
    credentials = "include"
  } = options;
  return {
    async *stream(request, opts) {
      const doFetch = fetchImpl ?? globalThis.fetch;
      if (!doFetch) {
        yield { type: "error", error: { message: "No fetch implementation available" } };
        return;
      }
      const resolvedHeaders = typeof headers === "function" ? await headers() : headers;
      let response;
      try {
        response = await doFetch(url, {
          method: "POST",
          headers: { "content-type": "application/json", accept: "text/event-stream", ...resolvedHeaders },
          body: buildBody ? buildBody(request) : JSON.stringify(request),
          signal: opts?.signal,
          credentials
        });
      } catch (err) {
        if (err?.name === "AbortError") return;
        yield { type: "error", error: { message: err?.message ?? "Request failed" } };
        return;
      }
      if (!response.ok || !response.body) {
        yield {
          type: "error",
          error: { message: `Chat request failed (${response.status} ${response.statusText})` }
        };
        return;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      try {
        for (; ; ) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let sep;
          while ((sep = buffer.indexOf("\n\n")) !== -1) {
            const frame = buffer.slice(0, sep);
            buffer = buffer.slice(sep + 2);
            const event = parseFrame(frame);
            if (event === "DONE") return;
            if (event) yield event;
          }
        }
        const tail = parseFrame(buffer);
        if (tail && tail !== "DONE") yield tail;
      } catch (err) {
        if (err?.name === "AbortError") return;
        yield { type: "error", error: { message: err?.message ?? "Stream error" } };
      } finally {
        reader.releaseLock();
      }
    }
  };
}
function parseFrame(frame) {
  const dataLines = frame.split("\n").filter((line) => line.startsWith("data:")).map((line) => line.slice(5).trim());
  if (dataLines.length === 0) return null;
  const data = dataLines.join("\n");
  if (data === "[DONE]") return "DONE";
  try {
    const parsed = JSON.parse(data);
    if (isStreamEvent(parsed)) return parsed;
    log.warn("Ignoring malformed SSE event", { data });
    return null;
  } catch {
    log.warn("Failed to parse SSE frame", { data });
    return null;
  }
}

// src/transport/text.ts
function createTextStreamChatTransport(options = {}) {
  const { url = DEFAULT_CHAT_ENDPOINT, fetchImpl, headers, buildBody, credentials = "include" } = options;
  return {
    async *stream(request, opts) {
      const doFetch = fetchImpl ?? globalThis.fetch;
      if (!doFetch) {
        yield { type: "error", error: { message: "No fetch implementation available" } };
        return;
      }
      const resolvedHeaders = typeof headers === "function" ? await headers() : headers;
      let response;
      try {
        response = await doFetch(url, {
          method: "POST",
          headers: { "content-type": "application/json", ...resolvedHeaders },
          body: buildBody ? buildBody(request) : JSON.stringify(request),
          signal: opts?.signal,
          credentials
        });
      } catch (err) {
        if (err?.name === "AbortError") return;
        yield { type: "error", error: { message: err?.message ?? "Request failed" } };
        return;
      }
      if (!response.ok || !response.body) {
        yield {
          type: "error",
          error: { message: `Chat request failed (${response.status} ${response.statusText})` }
        };
        return;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      try {
        for (; ; ) {
          const { value, done } = await reader.read();
          if (done) break;
          const text = decoder.decode(value, { stream: true });
          if (text) yield { type: "text-delta", text };
        }
        yield { type: "finish" };
      } catch (err) {
        if (err?.name === "AbortError") return;
        yield { type: "error", error: { message: err?.message ?? "Stream error" } };
      } finally {
        reader.releaseLock();
      }
    }
  };
}

// src/transport/json.ts
function defaultParseResponse(raw) {
  const root = raw && typeof raw === "object" ? raw : {};
  const body = root.data && typeof root.data === "object" ? root.data : root;
  const text = body.text ?? body.content ?? body.answer ?? body.message ?? "";
  return {
    text: typeof text === "string" ? text : "",
    actions: Array.isArray(body.actions) ? body.actions : void 0,
    citations: Array.isArray(body.citations) ? body.citations : void 0,
    finish: body.finish ?? (body.usage ? { usage: body.usage } : void 0)
  };
}
function createJsonChatTransport(options = {}) {
  const {
    url = DEFAULT_CHAT_ENDPOINT,
    fetchImpl,
    headers,
    buildBody,
    credentials = "include",
    parseResponse = defaultParseResponse
  } = options;
  return {
    async *stream(request, opts) {
      const doFetch = fetchImpl ?? globalThis.fetch;
      if (!doFetch) {
        yield { type: "error", error: { message: "No fetch implementation available" } };
        return;
      }
      const resolvedHeaders = typeof headers === "function" ? await headers() : headers;
      let response;
      try {
        response = await doFetch(url, {
          method: "POST",
          headers: { "content-type": "application/json", accept: "application/json", ...resolvedHeaders },
          body: buildBody ? buildBody(request) : JSON.stringify(request),
          signal: opts?.signal,
          credentials
        });
      } catch (err) {
        if (err?.name === "AbortError") return;
        yield { type: "error", error: { message: err?.message ?? "Request failed" } };
        return;
      }
      if (!response.ok) {
        yield {
          type: "error",
          error: { message: `Chat request failed (${response.status} ${response.statusText})` }
        };
        return;
      }
      let parsed;
      try {
        parsed = parseResponse(await response.json());
      } catch (err) {
        yield { type: "error", error: { message: err?.message ?? "Invalid chat response" } };
        return;
      }
      if (opts?.signal?.aborted) return;
      if (parsed.text) yield { type: "text-delta", text: parsed.text };
      for (const action of parsed.actions ?? []) yield { type: "action", action };
      for (const citation of parsed.citations ?? []) yield { type: "citation", citation };
      yield { type: "finish", finish: parsed.finish };
    }
  };
}
var createRequestResponseChatTransport = createJsonChatTransport;

// src/transport/mock.ts
var delay = (ms, signal) => new Promise((resolve, reject) => {
  if (signal?.aborted) return reject(new DOMException("Aborted", "AbortError"));
  const t = setTimeout(resolve, ms);
  signal?.addEventListener("abort", () => {
    clearTimeout(t);
    reject(new DOMException("Aborted", "AbortError"));
  });
});
function createMockChatTransport(responder) {
  return {
    async *stream(request, opts) {
      const resolved = typeof responder === "function" ? responder(request) : typeof responder === "string" ? { text: responder } : responder;
      try {
        if (Array.isArray(resolved)) {
          for (const event of resolved) {
            await delay(40, opts?.signal);
            yield event;
          }
          yield { type: "finish" };
          return;
        }
        const tokens = resolved.text.split(/(\s+)/).filter(Boolean);
        for (const token of tokens) {
          await delay(resolved.tokenDelayMs ?? 25, opts?.signal);
          yield { type: "text-delta", text: token };
        }
        for (const event of resolved.events ?? []) {
          await delay(40, opts?.signal);
          yield event;
        }
        yield { type: "finish" };
      } catch (err) {
        if (err?.name === "AbortError") return;
        yield { type: "error", error: { message: err?.message ?? "Mock error" } };
      }
    }
  };
}
function toRequestMessages(messages) {
  return messages.filter((m) => !m.pending && (m.role === "user" || m.role === "assistant")).map((m) => ({ role: m.role, content: messageText(m) })).filter((m) => m.content.length > 0);
}
function useAssistant(options) {
  const { transport, initialMessages, threadId, context, onAction, onCitation, onError } = options;
  const [messages, setMessages] = React.useState(initialMessages ?? []);
  const [status, setStatus] = React.useState("idle");
  const [error, setError] = React.useState(null);
  const [input, setInput] = React.useState("");
  const abortRef = React.useRef(null);
  const cbRef = React.useRef({ onAction, onCitation, onError });
  cbRef.current = { onAction, onCitation, onError };
  const appendPart = React.useCallback((messageId, part) => {
    setMessages(
      (prev) => prev.map((m) => {
        if (m.id !== messageId) return m;
        if (part.type === "text") {
          const last = m.parts[m.parts.length - 1];
          if (last && last.type === "text") {
            const parts = m.parts.slice(0, -1).concat({ type: "text", text: last.text + part.text });
            return { ...m, parts };
          }
        }
        return { ...m, parts: [...m.parts, part] };
      })
    );
  }, []);
  const run = React.useCallback(
    async (history) => {
      const controller = new AbortController();
      abortRef.current = controller;
      setStatus("streaming");
      setError(null);
      const placeholder = assistantPlaceholder();
      setMessages([...history, placeholder]);
      try {
        const stream = transport.stream(
          { messages: toRequestMessages(history), threadId, context },
          { signal: controller.signal }
        );
        for await (const event of stream) {
          switch (event.type) {
            case "text-delta":
              appendPart(placeholder.id, { type: "text", text: event.text });
              break;
            case "action":
              appendPart(placeholder.id, { type: "action", action: event.action });
              cbRef.current.onAction?.(event.action);
              break;
            case "citation":
              appendPart(placeholder.id, { type: "citation", citation: event.citation });
              cbRef.current.onCitation?.(event.citation);
              break;
            case "error": {
              const err = new Error(event.error.message);
              setError(err);
              setStatus("error");
              setMessages(
                (prev) => prev.map((m) => m.id === placeholder.id ? { ...m, pending: false, error: err.message } : m)
              );
              cbRef.current.onError?.(err);
              log.error("Assistant stream error", { message: err.message });
              return;
            }
            case "finish":
              break;
          }
        }
        setMessages((prev) => prev.map((m) => m.id === placeholder.id ? { ...m, pending: false } : m));
        setStatus("idle");
      } catch (err) {
        if (err?.name === "AbortError") {
          setMessages((prev) => prev.map((m) => m.id === placeholder.id ? { ...m, pending: false } : m));
          setStatus("idle");
          return;
        }
        const e = err;
        setError(e);
        setStatus("error");
        setMessages(
          (prev) => prev.map((m) => m.id === placeholder.id ? { ...m, pending: false, error: e.message } : m)
        );
        cbRef.current.onError?.(e);
        log.error("Assistant transport threw", { message: e.message });
      } finally {
        abortRef.current = null;
      }
    },
    [transport, threadId, context, appendPart]
  );
  const send = React.useCallback(
    async (text) => {
      const content = (text ?? input).trim();
      if (!content || status === "streaming") return;
      setInput("");
      const history = [...messages, userMessage(content)];
      setMessages(history);
      await run(history);
    },
    [input, status, messages, run]
  );
  const stop = React.useCallback(() => {
    abortRef.current?.abort();
  }, []);
  const regenerate = React.useCallback(async () => {
    if (status === "streaming") return;
    let lastUser = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "user") {
        lastUser = i;
        break;
      }
    }
    if (lastUser === -1) return;
    const history = messages.slice(0, lastUser + 1);
    setMessages(history);
    await run(history);
  }, [status, messages, run]);
  const clear = React.useCallback(() => {
    abortRef.current?.abort();
    setMessages(initialMessages ?? []);
    setStatus("idle");
    setError(null);
  }, [initialMessages]);
  return { messages, status, error, input, setInput, send, stop, regenerate, clear };
}
var useAiAssistant = useAssistant;

// src/tools/registry.ts
var DEFAULT_AUTHE_TOOLS = [
  {
    name: "query_transactions",
    description: "Search transactions by address, entity, chain, value, or time range.",
    category: "query"
  },
  {
    name: "summarize_entity",
    description: "Summarize what an entity is, its risk, and notable counterparties.",
    category: "query"
  },
  {
    name: "build_graph",
    description: "Build a fund-flow graph around an address/entity for the canvas.",
    category: "graph"
  },
  {
    name: "suggest_tags",
    description: "Propose tags/labels for an address or entity with rationale.",
    category: "catalog"
  },
  {
    name: "explain_risk",
    description: "Explain a risk score via its top weighted feature contributions.",
    category: "risk"
  },
  {
    name: "find_related",
    description: "Find related transactions, addresses, or entities (clusters, bridges).",
    category: "query"
  }
];
function registerAutheTools(extra = [], opts = {}) {
  const { includeDefaults = true } = opts;
  const specs = /* @__PURE__ */ new Map();
  for (const spec of includeDefaults ? DEFAULT_AUTHE_TOOLS : []) specs.set(spec.name, spec);
  for (const spec of extra) specs.set(spec.name, spec);
  const handlers = /* @__PURE__ */ new Map();
  return {
    list: () => [...specs.values()],
    get: (name) => specs.get(name),
    has: (name) => specs.has(name),
    setHandler: (name, handler) => {
      handlers.set(name, handler);
    },
    dispatch: async (name, args) => {
      const handler = handlers.get(name);
      if (!handler) throw new Error(`No client handler registered for tool "${name}"`);
      return handler(args);
    }
  };
}

// src/graph/overlay.ts
function overlayToTxGraphInput(overlay) {
  const nodes = (overlay.nodes ?? []).map((node) => ({
    id: node.id,
    data: {
      label: node.label,
      subtitle: node.subtitle,
      chain: node.chain,
      badge: node.badge,
      variant: node.variant
    }
  }));
  const edges = (overlay.edges ?? []).map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    data: {
      label: edge.label,
      variant: edge.variant,
      timestamp: edge.timestamp,
      dashed: false
    }
  }));
  return {
    nodes,
    edges,
    focusNodeId: overlay.focusNodeId,
    highlightNodeIds: (overlay.nodes ?? []).filter((n) => n.highlight).map((n) => n.id),
    highlightEdgeIds: (overlay.edges ?? []).filter((e) => e.highlight).map((e) => e.id)
  };
}
function mergeById(existing, incoming) {
  const map = new Map(existing.map((item) => [item.id, item]));
  for (const item of incoming) map.set(item.id, item);
  return [...map.values()];
}
var base = (props) => ({
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  ...props
});
function SparklesIcon(props) {
  return /* @__PURE__ */ jsxs("svg", { ...base(props), children: [
    /* @__PURE__ */ jsx("path", { d: "M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9z" }),
    /* @__PURE__ */ jsx("path", { d: "M19 14l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z" })
  ] });
}
function SendIcon(props) {
  return /* @__PURE__ */ jsxs("svg", { ...base(props), children: [
    /* @__PURE__ */ jsx("path", { d: "M22 2 11 13" }),
    /* @__PURE__ */ jsx("path", { d: "M22 2 15 22l-4-9-9-4z" })
  ] });
}
function StopIcon(props) {
  return /* @__PURE__ */ jsx("svg", { ...base(props), children: /* @__PURE__ */ jsx("rect", { x: "6", y: "6", width: "12", height: "12", rx: "1.5", fill: "currentColor", stroke: "none" }) });
}
function UserIcon(props) {
  return /* @__PURE__ */ jsxs("svg", { ...base(props), children: [
    /* @__PURE__ */ jsx("path", { d: "M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" }),
    /* @__PURE__ */ jsx("circle", { cx: "12", cy: "7", r: "4" })
  ] });
}
function ExternalLinkIcon(props) {
  return /* @__PURE__ */ jsxs("svg", { ...base(props), children: [
    /* @__PURE__ */ jsx("path", { d: "M15 3h6v6" }),
    /* @__PURE__ */ jsx("path", { d: "M10 14 21 3" }),
    /* @__PURE__ */ jsx("path", { d: "M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" })
  ] });
}
function ChevronRightIcon(props) {
  return /* @__PURE__ */ jsx("svg", { ...base(props), children: /* @__PURE__ */ jsx("path", { d: "m9 18 6-6-6-6" }) });
}
var KIND_BADGE = {
  graph_overlay: "Graph",
  table_filter: "Filter",
  navigate: "Navigate",
  open_resource: "Open",
  run_query: "Query",
  suggest_catalog: "Catalog"
};
function ActionChip({ action, onAction, disabled, className }) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "flex items-center justify-between gap-2 rounded-md border bg-muted/30 px-2.5 py-1.5 text-sm",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 items-center gap-2", children: [
          /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "shrink-0 text-[10px] uppercase tracking-wide", children: KIND_BADGE[action.kind] }),
          /* @__PURE__ */ jsx("span", { className: "truncate", children: actionLabel(action) })
        ] }),
        /* @__PURE__ */ jsxs(
          Button,
          {
            type: "button",
            size: "sm",
            variant: "ghost",
            disabled: disabled || !onAction,
            onClick: () => onAction?.(action),
            className: "shrink-0 gap-1",
            children: [
              "Apply",
              /* @__PURE__ */ jsx(ChevronRightIcon, { width: 14, height: 14 })
            ]
          }
        )
      ]
    }
  );
}
function Citations({ citations, onOpen, className }) {
  if (citations.length === 0) return null;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-wrap items-center gap-1.5 pt-1", className), children: [
    /* @__PURE__ */ jsx("span", { className: "text-[10px] uppercase tracking-wide text-muted-foreground", children: "Sources" }),
    citations.map(
      (citation) => citation.href ? /* @__PURE__ */ jsx("a", { href: citation.href, target: "_blank", rel: "noreferrer", title: citation.snippet, children: /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "cursor-pointer gap-1 hover:bg-accent", children: [
        citation.label,
        /* @__PURE__ */ jsx(ExternalLinkIcon, { width: 11, height: 11 })
      ] }) }, citation.id) : /* @__PURE__ */ jsx("button", { type: "button", onClick: () => onOpen?.(citation), title: citation.snippet, children: /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "cursor-pointer hover:bg-accent", children: citation.label }) }, citation.id)
    )
  ] });
}
function SuggestionChips({
  suggestions,
  onPick,
  disabled,
  className
}) {
  if (suggestions.length === 0) return null;
  return /* @__PURE__ */ jsx("div", { className: cn("flex flex-wrap gap-2", className), children: suggestions.map((prompt) => /* @__PURE__ */ jsx("button", { type: "button", disabled, onClick: () => onPick(prompt), children: /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "cursor-pointer px-2.5 py-1 hover:bg-secondary/70", children: prompt }) }, prompt)) });
}
function TypingDots() {
  return /* @__PURE__ */ jsx("span", { className: "inline-flex gap-1 py-1", "aria-label": "Assistant is typing", children: [0, 1, 2].map((i) => /* @__PURE__ */ jsx(
    "span",
    {
      className: "h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground/60",
      style: { animationDelay: `${i * 150}ms` }
    },
    i
  )) });
}
function AssistantMessage({
  message,
  onAction,
  onOpenCitation,
  actionsDisabled,
  userAvatar,
  className
}) {
  const isUser = message.role === "user";
  const citations = messageCitations(message);
  const hasText = message.parts.some((p) => p.type === "text" && p.text.length > 0);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex gap-3", isUser ? "flex-row-reverse" : "flex-row", className), children: [
    /* @__PURE__ */ jsx(
      Avatar,
      {
        size: "sm",
        className: isUser ? "bg-primary/10 text-primary" : "bg-brand/10 text-brand",
        src: isUser ? userAvatar?.src : void 0,
        alt: isUser ? userAvatar?.alt : void 0,
        fallback: isUser ? userAvatar?.fallback ?? /* @__PURE__ */ jsx(UserIcon, { width: 14, height: 14 }) : /* @__PURE__ */ jsx(SparklesIcon, { width: 14, height: 14 })
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: cn("flex min-w-0 max-w-[85%] flex-col gap-2", isUser ? "items-end" : "items-start"), children: [
      /* @__PURE__ */ jsxs(
        "div",
        {
          className: cn(
            "rounded-lg px-3 py-2 text-sm",
            isUser ? "bg-primary text-primary-foreground" : "border bg-card"
          ),
          children: [
            message.parts.map((part, index) => {
              if (part.type === "text") {
                return /* @__PURE__ */ jsx("p", { className: "whitespace-pre-wrap break-words", children: part.text }, index);
              }
              if (part.type === "action") {
                return /* @__PURE__ */ jsx("div", { className: "mt-2 first:mt-0", children: /* @__PURE__ */ jsx(ActionChip, { action: part.action, onAction, disabled: actionsDisabled }) }, index);
              }
              return null;
            }),
            message.pending && !hasText ? /* @__PURE__ */ jsx(TypingDots, {}) : null,
            message.error ? /* @__PURE__ */ jsx("p", { className: "mt-1 text-xs text-destructive", children: message.error }) : null
          ]
        }
      ),
      !isUser && /* @__PURE__ */ jsx(Citations, { citations, onOpen: onOpenCitation })
    ] })
  ] });
}
function AssistantComposer({
  value,
  onChange,
  onSend,
  onStop,
  streaming,
  disabled,
  placeholder = "Ask about a transaction, address, or entity\u2026",
  className
}) {
  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (!streaming && value.trim()) onSend();
    }
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-end gap-2 border-t bg-background p-2", className), children: [
    /* @__PURE__ */ jsx(
      Textarea,
      {
        rows: 1,
        value,
        disabled,
        placeholder,
        onChange: (event) => onChange(event.target.value),
        onKeyDown: handleKeyDown,
        className: "max-h-40 min-h-9 flex-1 resize-none"
      }
    ),
    streaming ? /* @__PURE__ */ jsx(Button, { type: "button", size: "icon", variant: "outline", onClick: onStop, "aria-label": "Stop", children: /* @__PURE__ */ jsx(StopIcon, {}) }) : /* @__PURE__ */ jsx(
      Button,
      {
        type: "button",
        size: "icon",
        variant: "brand",
        disabled: disabled || !value.trim(),
        onClick: onSend,
        "aria-label": "Send",
        children: /* @__PURE__ */ jsx(SendIcon, {})
      }
    )
  ] });
}
function AssistantPanel({
  transport,
  title = "Assistant",
  subtitle,
  suggestions = [],
  initialMessages,
  threadId,
  context,
  onAction,
  onOpenCitation,
  placeholder,
  emptyTitle = "Ask anything",
  emptyDescription = "Query transactions, summarize an entity, or build a graph \u2014 answers come back with sources and one-click actions.",
  header,
  className
}) {
  const assistant = useAssistant({ transport, initialMessages, threadId, context, onAction });
  const { messages, status, error, input, setInput, send, stop } = assistant;
  const scrollRef = React.useRef(null);
  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);
  const isEmpty = messages.length === 0;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex h-full min-h-0 flex-col overflow-hidden rounded-lg border bg-background", className), children: [
    header ?? /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 border-b px-3 py-2", children: [
      /* @__PURE__ */ jsx("span", { className: "inline-flex h-6 w-6 items-center justify-center rounded-md bg-brand/10 text-brand", children: /* @__PURE__ */ jsx(SparklesIcon, { width: 14, height: 14 }) }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
        /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold leading-none", children: title }),
        subtitle ? /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: subtitle }) : null
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { ref: scrollRef, className: "flex-1 space-y-4 overflow-y-auto p-3", children: [
      isEmpty ? /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center gap-4 py-8 text-center", children: [
        /* @__PURE__ */ jsx("span", { className: "inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand/10 text-brand", children: /* @__PURE__ */ jsx(SparklesIcon, { width: 20, height: 20 }) }),
        /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
          /* @__PURE__ */ jsx("p", { className: "text-sm font-medium", children: emptyTitle }),
          /* @__PURE__ */ jsx("p", { className: "mx-auto max-w-sm text-xs text-muted-foreground", children: emptyDescription })
        ] }),
        /* @__PURE__ */ jsx(SuggestionChips, { suggestions, onPick: (prompt) => void send(prompt), className: "justify-center" })
      ] }) : messages.map((message) => /* @__PURE__ */ jsx(
        AssistantMessage,
        {
          message,
          onAction,
          onOpenCitation,
          actionsDisabled: status === "streaming"
        },
        message.id
      )),
      error && !messages.some((m) => m.error) ? /* @__PURE__ */ jsx("p", { className: "text-center text-xs text-destructive", children: error.message }) : null
    ] }),
    /* @__PURE__ */ jsx(
      AssistantComposer,
      {
        value: input,
        onChange: setInput,
        onSend: () => void send(),
        onStop: stop,
        streaming: status === "streaming",
        placeholder
      }
    )
  ] });
}
var AiChatPanel = AssistantPanel;
function AssistantLauncher({
  buttonLabel,
  open,
  onOpenChange,
  hideTrigger,
  triggerClassName,
  title = "Assistant",
  ...panelProps
}) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isOpen = open ?? internalOpen;
  const setOpen = (next) => {
    setInternalOpen(next);
    onOpenChange?.(next);
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    !hideTrigger && /* @__PURE__ */ jsxs(
      Button,
      {
        type: "button",
        variant: "brand",
        onClick: () => setOpen(true),
        className: cn(
          "fixed bottom-5 right-5 z-40 gap-2 rounded-full shadow-lg",
          buttonLabel ? "px-4" : "h-12 w-12 p-0",
          triggerClassName
        ),
        "aria-label": buttonLabel ?? "Open assistant",
        children: [
          /* @__PURE__ */ jsx(SparklesIcon, { width: 18, height: 18 }),
          buttonLabel
        ]
      }
    ),
    /* @__PURE__ */ jsx(Dialog, { open: isOpen, onOpenChange: setOpen, children: /* @__PURE__ */ jsxs(DialogContent, { className: "flex h-[70vh] max-h-[680px] w-full max-w-lg flex-col gap-0 overflow-hidden p-0", children: [
      /* @__PURE__ */ jsx(DialogHeader, { className: "border-b px-3 py-2", children: /* @__PURE__ */ jsxs(DialogTitle, { className: "flex items-center gap-2 text-sm", children: [
        /* @__PURE__ */ jsx("span", { className: "inline-flex h-6 w-6 items-center justify-center rounded-md bg-brand/10 text-brand", children: /* @__PURE__ */ jsx(SparklesIcon, { width: 14, height: 14 }) }),
        title
      ] }) }),
      /* @__PURE__ */ jsx("div", { className: "min-h-0 flex-1", children: /* @__PURE__ */ jsx(AssistantPanel, { ...panelProps, title, header: /* @__PURE__ */ jsx(Fragment, {}), className: "h-full rounded-none border-0" }) })
    ] }) })
  ] });
}
var REASON_LABEL = {
  low_confidence: "Low confidence",
  high_risk: "High risk",
  shadow_disagreement: "Shadow disagreement",
  sampled: "Sampled",
  manual: "Manual"
};
var STATUS_TONE = {
  pending: "border-amber-500/30 bg-amber-500/10 text-amber-600",
  approved: "border-risk-none/30 bg-risk-none/10 text-risk-none",
  rejected: "border-risk-high/30 bg-risk-high/10 text-risk-high",
  escalated: "border-risk-critical/30 bg-risk-critical/10 text-risk-critical"
};
function ReviewQueuePanel({
  items,
  onDecide,
  decidedBy,
  bands,
  readOnly,
  isLoading,
  error,
  onRetry,
  className
}) {
  if (error) return /* @__PURE__ */ jsx(ErrorState, { error, onRetry });
  if (isLoading) {
    return /* @__PURE__ */ jsx("div", { className: cn("flex flex-col gap-2", className), "aria-busy": true, children: Array.from({ length: 4 }).map((_, index) => /* @__PURE__ */ jsx(Skeleton, { className: "h-24 w-full" }, index)) });
  }
  if (items.length === 0) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "Review queue empty",
        description: "Predictions that need a human decision will appear here."
      }
    );
  }
  const decide = (item, status) => {
    void onDecide?.(item.id, { status, decidedBy });
  };
  const pending = pendingReviewCount(items);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-3", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsx("h3", { className: "text-sm font-semibold", children: "Review queue" }),
      /* @__PURE__ */ jsxs(Badge, { variant: "outline", children: [
        pending,
        " pending"
      ] })
    ] }),
    items.map((item) => {
      const level = predictionRiskLevel(item.prediction, bands);
      const actionable = isReviewActionable(item) && !readOnly;
      const contributions = topContributions(item.prediction, 3);
      return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2 rounded-md border bg-card p-3", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-2", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-col gap-1", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
              /* @__PURE__ */ jsx("span", { className: "font-medium capitalize", children: item.prediction.target.kind }),
              /* @__PURE__ */ jsx("code", { className: "truncate rounded bg-muted px-1.5 py-0.5 text-xs", children: item.prediction.target.id }),
              level ? /* @__PURE__ */ jsx(RiskBadge, { level, score: item.prediction.score ?? void 0 }) : null
            ] }),
            /* @__PURE__ */ jsxs("span", { className: "text-xs text-muted-foreground", children: [
              item.prediction.modelId,
              "@",
              item.prediction.modelVersion,
              item.reason ? /* @__PURE__ */ jsxs(Fragment, { children: [
                " \xB7 ",
                REASON_LABEL[item.reason]
              ] }) : null
            ] })
          ] }),
          /* @__PURE__ */ jsx(Badge, { variant: "outline", className: cn("shrink-0 border", STATUS_TONE[item.status]), children: ML_REVIEW_STATUS_LABEL[item.status] })
        ] }),
        contributions.length > 0 && /* @__PURE__ */ jsx("ul", { className: "flex flex-wrap gap-1.5", children: contributions.map((c) => /* @__PURE__ */ jsxs(
          "li",
          {
            className: cn(
              "rounded border px-1.5 py-0.5 text-[11px]",
              c.weight >= 0 ? "text-risk-high" : "text-risk-none"
            ),
            title: c.value != null ? String(c.value) : void 0,
            children: [
              c.weight >= 0 ? "+" : "\u2212",
              " ",
              c.feature
            ]
          },
          c.feature
        )) }),
        actionable && /* @__PURE__ */ jsxs("div", { className: "flex gap-2 pt-1", children: [
          /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", variant: "outline", onClick: () => decide(item, MLReviewStatus.APPROVED), children: "Approve" }),
          /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", variant: "outline", onClick: () => decide(item, MLReviewStatus.REJECTED), children: "Reject" }),
          /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", variant: "ghost", onClick: () => decide(item, MLReviewStatus.ESCALATED), children: "Escalate" })
        ] }),
        item.rationale ? /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
          "\u201C",
          item.rationale,
          "\u201D"
        ] }) : null
      ] }, item.id);
    })
  ] });
}
var RISK_TO_CRITICALITY = {
  [RiskLevel.SEVERE]: EventCriticality.CRITICAL,
  [RiskLevel.HIGH]: EventCriticality.HIGH,
  [RiskLevel.MEDIUM]: EventCriticality.MEDIUM,
  [RiskLevel.LOW]: EventCriticality.LOW,
  [RiskLevel.SAFE]: EventCriticality.SUCCESS
};
function predictionsToActivityLog(predictions, bands) {
  return predictions.map((prediction) => {
    const level = predictionRiskLevel(prediction, bands);
    const top = topContributions(prediction, 3).map((c) => `${c.weight >= 0 ? "+" : "\u2212"}${c.feature}`).join(", ");
    return {
      id: prediction.id,
      created_at: prediction.createdAt,
      resource: EventResourceType.ACTIVITY_LOG,
      criticality_level: level ? RISK_TO_CRITICALITY[level] : EventCriticality.LOW,
      message_summary: `Scored ${prediction.target.kind} ${prediction.target.id}${prediction.score != null ? ` \u2192 ${prediction.score}` : ""}${level ? ` (${level})` : ""}`,
      function_name: `${prediction.modelId}@${prediction.modelVersion}`,
      payload_summary: top || void 0,
      created_by: prediction.modelId,
      metadata: { latencyMs: prediction.latencyMs, confidence: prediction.confidence }
    };
  });
}
function PredictionAuditTrail({
  predictions,
  bands,
  className
}) {
  if (predictions.length === 0) {
    return /* @__PURE__ */ jsx(EmptyState, { title: "No predictions yet", description: "Model predictions will appear here as they run." });
  }
  return /* @__PURE__ */ jsx(AuditTrail, { events: predictionsToActivityLog(predictions, bands), className: cn(className) });
}
function Stat({ label, value }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col rounded-md border bg-card px-3 py-2", children: [
    /* @__PURE__ */ jsx("span", { className: "text-lg font-semibold tabular-nums", children: value }),
    /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: label })
  ] });
}
function ModelComparison({
  champion,
  shadow,
  models,
  bands,
  className
}) {
  const comparison = React.useMemo(
    () => comparePredictions(champion, shadow, bands),
    [champion, shadow, bands]
  );
  const diffs = React.useMemo(() => disagreements(comparison), [comparison]);
  if (comparison.pairs.length === 0 && champion.length === 0 && shadow.length === 0) {
    return /* @__PURE__ */ jsx(EmptyState, { title: "No predictions to compare", description: "Run the champion and shadow models to compare." });
  }
  const championModel = models ? championOf(models) : void 0;
  const shadowModel = models?.find((m) => m.role !== "champion");
  const agreementPct = Math.round(comparison.agreementRate * 1e3) / 10;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-3", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2 text-sm", children: [
      /* @__PURE__ */ jsxs(Badge, { variant: "outline", children: [
        "Champion \xB7 ",
        championModel?.name ?? comparison.championKey
      ] }),
      /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "vs" }),
      /* @__PURE__ */ jsxs(Badge, { variant: "secondary", children: [
        "Shadow \xB7 ",
        shadowModel?.name ?? comparison.shadowKey
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-2 gap-2 sm:grid-cols-4", children: [
      /* @__PURE__ */ jsx(Stat, { label: "Agreement", value: `${agreementPct}%` }),
      /* @__PURE__ */ jsx(Stat, { label: "Compared", value: comparison.pairs.length }),
      /* @__PURE__ */ jsx(Stat, { label: "Champion only", value: comparison.championOnly }),
      /* @__PURE__ */ jsx(Stat, { label: "Shadow only", value: comparison.shadowOnly })
    ] }),
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsxs("h4", { className: "mb-2 text-sm font-semibold", children: [
        "Disagreements ",
        /* @__PURE__ */ jsxs("span", { className: "text-muted-foreground", children: [
          "(",
          diffs.length,
          ")"
        ] })
      ] }),
      diffs.length === 0 ? /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: "The two models agreed on every shared target." }) : /* @__PURE__ */ jsx("ul", { className: "flex flex-col gap-1.5", children: diffs.map((pair) => /* @__PURE__ */ jsxs(
        "li",
        {
          className: "flex items-center justify-between gap-2 rounded-md border bg-card px-3 py-2 text-sm",
          children: [
            /* @__PURE__ */ jsx("code", { className: "truncate text-xs", children: pair.targetKey }),
            /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center gap-2", children: [
              pair.championLevel ? /* @__PURE__ */ jsx(RiskBadge, { level: pair.championLevel }) : /* @__PURE__ */ jsx(Badge, { variant: "outline", children: "\u2014" }),
              /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "\u2192" }),
              pair.shadowLevel ? /* @__PURE__ */ jsx(RiskBadge, { level: pair.shadowLevel }) : /* @__PURE__ */ jsx(Badge, { variant: "outline", children: "\u2014" }),
              pair.scoreDelta != null ? /* @__PURE__ */ jsxs(
                "span",
                {
                  className: cn(
                    "tabular-nums text-xs",
                    pair.scoreDelta > 0 ? "text-risk-high" : "text-risk-none"
                  ),
                  children: [
                    pair.scoreDelta > 0 ? "+" : "",
                    pair.scoreDelta
                  ]
                }
              ) : null
            ] })
          ]
        },
        pair.targetKey
      )) })
    ] })
  ] });
}

export { AI_ASSISTANT_PACKAGE, ActionChip, AiChatPanel, AssistantComposer, AssistantLauncher, AssistantMessage, AssistantPanel, ChevronRightIcon, Citations, DEFAULT_AUTHE_TOOLS, DEFAULT_CHAT_ENDPOINT, ExternalLinkIcon, ModelComparison, PredictionAuditTrail, ReviewQueuePanel, SendIcon, SparklesIcon, StopIcon, SuggestionChips, UserIcon, actionLabel, assistantPlaceholder, createJsonChatTransport, createMockChatTransport, createRequestResponseChatTransport, createSseChatTransport, createTextStreamChatTransport, log, mergeById, messageActions, messageCitations, messageText, newId, overlayToTxGraphInput, predictionsToActivityLog, registerAutheTools, useAiAssistant, useAssistant, userMessage };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map